import { inject, Injectable } from '@angular/core';
import { CanActivate, GuardResult } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { CustomLoginGuard } from '@spartacus/core';
import { AsmEnablerService, CsAgentAuthService } from '@spartacus/asm/root';

export const totalRetries = 1;
/**
 * This guard requests the CSRF token required for the custom login form as a way
 * to check for a valid Authorization Server session.
 *
 * It will redirect the user to restart the auth flow on failed requests, keeping
 * track of the redirects to avoid creating an infinite loop of redirects.
 */
 /*
  * The Siko override only redirects to another route,
  * specifically the preconfigured “home” route, which is protected in Siko B2B, causing an infinite loop.
  */
@Injectable({
   providedIn: 'root',
})
export class SikoB2bCustomLoginGuard extends CustomLoginGuard implements CanActivate {

   csAgentAuthService = inject(CsAgentAuthService);

   asmEnablerService = inject(AsmEnablerService);

   customerSupportAgentLoggedIn$: Observable<boolean> = this.csAgentAuthService.isCustomerSupportAgentLoggedIn();

   canActivate(): Observable<GuardResult> {
      if (!this.authConfigService.customLoginEnabled() || !this.windowRef.isBrowser()) {
         // disable guard when custom login page is not enabled or when the application is running in SSR mode.
         return of(true);
      }

      return this.authService.getCsrfToken().pipe(
         tap(token => {
            this.csrfStateService.set(token);
            this.clearRedirectCount();
         }),
         map(() => true),
         catchError(() => {
            const currentCount = this.getRedirectCount();
            // check if retry limit is met
            if (currentCount >= totalRetries) {
               // go to homepage with message
               this.clearRedirectCount();
               this.showErrorMessage({ key: 'authMessages.unrecoverableError' });
               return this.createRoute('register');
            }

            // retry until limit met
            this.setRedirectCount(currentCount + 1);

            return this.customerSupportAgentLoggedIn$.pipe(
               switchMap(isAgentLoggedIn => {
                  if (isAgentLoggedIn) {
                     /*
                     * When an emulated customer is logged out the app navigates to the login page.
                     * That navigation triggers AsmAuthService.warnAboutLoggedCSAgent and produces a
                     * global error message which is undesirable for customer support agents.
                     * Therefore, if a CS agent is currently logged in, we redirect to the homepage
                     * instead of the login page to suppress that message.
                     */
                     return this.createRoute('home');
                  }

                  /*
                   * ASM is enabled but no CS agent is logged in yet - the ASM bootstrap case
                   * (e.g. ?asm=true on a protected route). In incognito the CSRF request for the
                   * custom login form fails (no authorization server session / JSESSIONID), and
                   * the default recovery navigates to the 'login' cxRoute (= path '/sign-in'),
                   * whose LoginGuard calls loginWithRedirect() -> /authorizationserver/login.
                   *
                   * Instead render this page so the ASM cockpit can mount. The agent authenticates
                   * via password flow (a direct /oauth/token request - no redirect, no CSRF token
                   * needed), then emulates a customer whose token satisfies the protected routes.
                   */
                  if (this.asmEnablerService.isEnabled()) {
                     return of(true);
                  }

                  return this.createRoute('login');
               })
            );
         })
      );
   }
}
